"use client";

import React, { useEffect, useRef, useState } from "react";
import { Separator } from "@/components/ui/separator";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
function AnimatedCounter({
  value,
  suffix = ""
}: {
  value: number;
  suffix?: string;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLSpanElement>({
    once: true,
    threshold: 0.5
  });
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (isVisible) {
      let start = 0;
      const duration = 2000;
      const frameRate = 1000 / 60;
      const totalFrames = duration / frameRate;
      const increment = value / totalFrames;
      const timer = setInterval(() => {
        start += increment;
        if (start >= value) {
          setCount(value);
          clearInterval(timer);
        } else {
          setCount(Math.floor(start));
        }
      }, frameRate);
      return () => clearInterval(timer);
    }
  }, [isVisible, value]);
  return <span ref={ref} className="tabular-nums">
      {count}
      {suffix}
    </span>;
}
const statsData = [{
  label: "Years of Experience",
  value: 10,
  suffix: "+"
}, {
  label: "Safety Audits",
  value: 500,
  suffix: "+"
}, {
  label: "Hazards Identified",
  value: 2500,
  suffix: "+"
}, {
  label: "Compliance Rating",
  value: 100,
  suffix: "%"
}];
export default function PowerInMotion() {
  return <section data-section-id="120913" id="safety-by-the-numbers" className="dark bg-background text-foreground relative py-20 overflow-hidden border-y border-border/20">
      {}
      <div className="absolute inset-0 opacity-[0.04] pointer-events-none" style={{
      backgroundImage: `linear-gradient(to right, #ffffff 1px, transparent 1px), linear-gradient(to bottom, #ffffff 1px, transparent 1px)`,
      backgroundPosition: `0 0`,
      backgroundSize: `10px 10px`,
      backgroundRepeat: `repeat`
    }} aria-hidden="true" />

      {}
      <div className="absolute inset-0 bg-gradient-to-b from-background via-transparent to-background pointer-events-none" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-primary/10 via-transparent to-transparent pointer-events-none" />

      <div className="container mx-auto px-4 relative z-10">
        <div className="flex flex-col md:flex-row items-center justify-between gap-12 md:gap-0">
          {statsData.map((stat, i) => <React.Fragment key={stat.label}>
              <div data-index={i} className="flex flex-col items-center justify-center flex-1 text-center group">
                <div className="text-5xl lg:text-7xl font-bold font-mono tracking-tight text-primary mb-2 drop-shadow-[0_0_20px_rgba(251,146,60,0.2)] transition-transform duration-500 group-hover:scale-105">
                  <AnimatedCounter value={stat.value} suffix={stat.suffix} />
                </div>
                <div className="text-xs lg:text-sm font-bold text-foreground uppercase tracking-[0.2em]">
                  {stat.label}
                </div>
              </div>

              {}
              {i < statsData.length - 1 && <div className="hidden md:block h-24 w-px bg-gradient-to-b from-transparent via-border/40 to-transparent shrink-0" />}
              
              {}
              {i < statsData.length - 1 && <Separator className="md:hidden w-24 bg-border/30" />}
            </React.Fragment>)}
        </div>
      </div>
    </section>;
}